import { useMemo, useRef } from "react";
import { useDotBotsAuth } from "@dotbots-boutique/auth-sdk/react";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

if (!BACKEND_URL) {
  console.warn("VITE_BACKEND_URL is not set — API calls will fail");
}

type FetchFn = (input: string, init?: RequestInit) => Promise<Response>;

async function request<T>(fetchFn: FetchFn, path: string, init?: RequestInit): Promise<T> {
  const url = `${BACKEND_URL.replace(/\/$/, "")}${path}`;
  const response = await fetchFn(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`API ${response.status}: ${text || response.statusText}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

// Send a multipart/form-data request — no auto Content-Type header so
// the browser sets it (with boundary). Used by uploads that stream
// instead of base64-ing into JSON.
async function requestForm<T>(fetchFn: FetchFn, path: string, form: FormData): Promise<T> {
  const url = `${BACKEND_URL.replace(/\/$/, "")}${path}`;
  const response = await fetchFn(url, { method: "POST", body: form });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`API ${response.status}: ${text || response.statusText}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export interface ApiClient {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
  postForm: <T>(path: string, form: FormData) => Promise<T>;
  put: <T>(path: string, body?: unknown) => Promise<T>;
  patch: <T>(path: string, body?: unknown) => Promise<T>;
  del: <T>(path: string) => Promise<T>;
}

// `useApi` MUST return a stable reference. Otherwise components that put
// `api` in a useEffect dependency array would refire every render — the SDK's
// `auth.fetch` is recreated on each render, so any naive object literal here
// would change identity every time and trigger an infinite poll loop.
export function useApi(): ApiClient {
  const { fetch: authFetch } = useDotBotsAuth();
  const fetchRef = useRef<FetchFn>(authFetch);
  fetchRef.current = authFetch;

  return useMemo<ApiClient>(
    () => ({
      get: (path) => request(fetchRef.current, path),
      post: (path, body) =>
        request(fetchRef.current, path, {
          method: "POST",
          body: body === undefined ? undefined : JSON.stringify(body),
        }),
      postForm: (path, form) => requestForm(fetchRef.current, path, form),
      put: (path, body) =>
        request(fetchRef.current, path, {
          method: "PUT",
          body: body === undefined ? undefined : JSON.stringify(body),
        }),
      patch: (path, body) =>
        request(fetchRef.current, path, {
          method: "PATCH",
          body: body === undefined ? undefined : JSON.stringify(body),
        }),
      del: (path) => request(fetchRef.current, path, { method: "DELETE" }),
    }),
    [],
  );
}

// Used by public (non-authenticated) pages — no token added.
export async function publicPost<T>(path: string, body: unknown): Promise<T> {
  const url = `${BACKEND_URL.replace(/\/$/, "")}${path}`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`API ${response.status}: ${text || response.statusText}`);
  }
  return response.json() as Promise<T>;
}

export async function publicGet<T>(path: string): Promise<T> {
  const url = `${BACKEND_URL.replace(/\/$/, "")}${path}`;
  const response = await fetch(url);
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`API ${response.status}: ${text || response.statusText}`);
  }
  return response.json() as Promise<T>;
}
